import { FileDownloadOutlined } from "@mui/icons-material";
import {
  Button,
  Card,
  CardHeader,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import type { FunctionalGroup } from "@types";
import type { ReactNode } from "react";
import type { TaskProgram, TrainingScope } from "src/services/backend";
import { TrainingProgramMatrix } from "./TrainingProgramMatrix";
import { TrainingUserMatrix } from "./TrainingUserMatrix";
import type { TaskProgramUserMatrixRow } from "./training-user-matrix";

export type TrainingMatrixView = "program" | "user";

type TrainingMatrixProps = {
  view: TrainingMatrixView;
  onViewChange: (view: TrainingMatrixView) => void;
  programs: TaskProgram[];
  functionalGroups: FunctionalGroup[];
  programsLoading: boolean;
  userRows: TaskProgramUserMatrixRow[];
  userRowsLoading: boolean;
  userRowsError: boolean;
  canViewByUser: boolean;
  scope: TrainingScope;
  action?: ReactNode;
  onExport: () => void;
  onSelectProgram: (programId: string) => void;
};

export const TrainingMatrix = ({
  view,
  onViewChange,
  programs,
  functionalGroups,
  programsLoading,
  userRows,
  userRowsLoading,
  userRowsError,
  canViewByUser,
  scope,
  action,
  onExport,
  onSelectProgram,
}: TrainingMatrixProps) => {
  return (
    <Card variant="outlined" sx={{ overflow: "hidden" }}>
      <CardHeader
        title="Training Matrix"
        subheader={
          view === "program"
            ? "Functional groups assigned to each training program"
            : "Trainees, their functional groups and assignment progress"
        }
        titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
        subheaderTypographyProps={{ variant: "caption" }}
        action={
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={view}
              onChange={(_event, value: TrainingMatrixView | null) => {
                if (value) onViewChange(value);
              }}
              aria-label="Training matrix view"
              sx={{
                border: 0,
                backgroundColor: "action.hover",
                borderRadius: 1,
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: 0.75,
                  px: 1.25,
                  py: 0.5,
                  textTransform: "none",
                },
              }}
            >
              {canViewByUser && (
                <ToggleButton value="user" aria-label="By user">
                  By user
                </ToggleButton>
              )}
              <ToggleButton value="program" aria-label="By program">
                By program
              </ToggleButton>
            </ToggleButtonGroup>
            {canViewByUser && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadOutlined />}
                onClick={onExport}
                disabled={userRowsLoading || userRowsError || userRows.length === 0}
                sx={{ borderColor: "divider", color: "text.primary" }}
              >
                Export All Trainings CSV
              </Button>
            )}
            {action}
          </Stack>
        }
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          gap: 2,
          flexWrap: "wrap",
          "& .MuiCardHeader-action": { alignSelf: "center", m: 0 },
        }}
      />

      {view === "program" ? (
        <TrainingProgramMatrix
          programs={programs}
          functionalGroups={functionalGroups}
          isLoading={programsLoading}
          scope={scope}
          onSelectProgram={onSelectProgram}
        />
      ) : (
        <TrainingUserMatrix
          rows={userRows}
          isLoading={userRowsLoading}
          isError={userRowsError}
          onSelectProgram={onSelectProgram}
        />
      )}
    </Card>
  );
};
